import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Select, DatePicker, Upload, message, Popconfirm, Input, Tag } from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined, FilePdfOutlined, FileImageOutlined } from '@ant-design/icons';
import api from '../api';
import moment from 'moment';

const { Option } = Select;

function getClearanceColor(clearanceDate) {
  const today = new Date();
  const clearance = new Date(clearanceDate);
  const diffDays = (clearance - today) / (1000 * 60 * 60 * 24);
  if (clearance < today) return 'red';
  if (diffDays < 30) return 'orange';
  return 'green';
}

const SubcontractorInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [clearanceDate, setClearanceDate] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchInvoices();
    fetchSubcontractors();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    const res = await api.get('/subcontractor-invoices');
    setLoading(false);
    if (res.success) setInvoices(res.data);
    else message.error(res.message || 'Failed to fetch invoices');
  };

  const fetchSubcontractors = async () => {
    const res = await api.get('/subcontractors');
    if (res.success) setSubcontractors(res.data);
  };

  const openModal = () => {
    setModalVisible(true);
    setFileList([]);
    setClearanceDate(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!fileList.length) {
        message.error('Please upload an invoice file');
        return;
      }
      setUploading(true);
      const formData = new FormData();
      formData.append('subcontractor_id', values.subcontractor_id);
      formData.append('uploaded_date', values.uploaded_date.format('YYYY-MM-DD'));
      formData.append('invoice_file', fileList[0].originFileObj);
      const res = await api.post('/subcontractor-invoices', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploading(false);
      if (res.success) {
        message.success('Invoice uploaded');
        setModalVisible(false);
        fetchInvoices();
      } else {
        message.error(res.message || 'Upload failed');
      }
    } catch (err) {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    const res = await api.delete(`/subcontractor-invoices/${id}`);
    if (res.success) {
      message.success('Invoice deleted');
      fetchInvoices();
    } else {
      message.error(res.message || 'Delete failed');
    }
  };

  // Auto-calculate clearance date on form change
  const handleFormChange = async (_, allValues) => {
    const { subcontractor_id, uploaded_date } = allValues;
    if (subcontractor_id && uploaded_date) {
      const sub = subcontractors.find(s => s.id === subcontractor_id);
      const days = sub?.invoice_clearance_days || 0;
      const date = moment(uploaded_date).add(days, 'days');
      setClearanceDate(date.format('YYYY-MM-DD'));
    } else {
      setClearanceDate(null);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: 'Subcontractor', dataIndex: ['subcontractor', 'name'], key: 'subcontractor', render: (_, r) => r.subcontractor?.name },
    { title: 'Uploaded Date', dataIndex: 'uploaded_date', key: 'uploaded_date', render: (date) => date ? moment(date).format('DD/MM/YYYY') : '-' },
    { 
      title: 'Clearance Date', 
      dataIndex: 'clearance_date', 
      key: 'clearance_date',
      render: (date) => {
        const color = getClearanceColor(date);
        let text = 'Valid';
        if (color === 'red') text = 'Overdue';
        else if (color === 'orange') text = 'Expiring Soon';
        const formattedDate = date ? moment(date).format('DD/MM/YYYY') : '-';
        return <Tag color={color}>{text} - {formattedDate}</Tag>;
      }
    },
    {
      title: 'File',
      dataIndex: 'invoice_file',
      key: 'invoice_file',
      render: (file, record) => {
        if (!file) return null;
        const isPdf = /\.pdf$/i.test(file);
        const isImg = /\.(jpg|jpeg|png)$/i.test(file);
        return (
          <a href={`/storage/${file}`} target="_blank" rel="noopener noreferrer">
            {isPdf ? <FilePdfOutlined style={{ color: '#cf1322', fontSize: 20 }} /> : isImg ? <FileImageOutlined style={{ color: '#1890ff', fontSize: 20 }} /> : <UploadOutlined />}
            &nbsp;View
          </a>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Popconfirm title="Delete this invoice?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
          <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>Subcontractor Invoices</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>Upload Invoice</Button>
      </div>
      <Table
        columns={columns}
        dataSource={invoices}
        loading={loading}
        rowKey="id"
      />
      <Modal
        title="Upload Subcontractor Invoice"
        open={modalVisible}
        onOk={handleOk}
        onCancel={() => setModalVisible(false)}
        okText={uploading ? 'Uploading...' : 'Upload'}
        confirmLoading={uploading}
      >
        <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
          <Form.Item name="subcontractor_id" label="Subcontractor" rules={[{ required: true, message: 'Select subcontractor' }]}> 
            <Select placeholder="Select subcontractor">
              {subcontractors.map(sub => <Option key={sub.id} value={sub.id}>{sub.name}</Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="uploaded_date" label="Uploaded Date" rules={[{ required: true, message: 'Select uploaded date' }]}> 
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item label="Invoice File" required>
            <Upload
              beforeUpload={() => false}
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              accept=".pdf,.jpg,.jpeg,.png"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
          <Form.Item label="Clearance Date">
            <Input value={clearanceDate ? moment(clearanceDate).format('DD/MM/YYYY') : ''} disabled placeholder="Auto-calculated" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubcontractorInvoices; 